// Localização: frontend/src/hooks/usePacientePagamentoStatus.js
// Hook para buscar status de pagamento de mensalidade dos pacientes

import { useState, useEffect, useCallback } from 'react';
import financeiroApi from '../api/financeiroApi';

/**
 * Hook para buscar status de pagamento de mensalidade de um paciente
 * @param {number} pacienteId - ID do paciente
 * @returns {Object} { status, isLoading, error, refetch }
 */
export const usePacientePagamentoStatus = (pacienteId) => {
    const [status, setStatus] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchStatus = useCallback(async () => {
        if (!pacienteId) {
            setStatus(null);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const today = new Date();
            const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
            const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
            
            // Buscar contas a receber do paciente no mês atual
            const response = await financeiroApi.contaReceber.getAll({
                paciente_id: pacienteId,
                data_inicio: firstDayOfMonth.toISOString().split('T')[0],
                data_fim: lastDayOfMonth.toISOString().split('T')[0],
                limit: 100 // Buscar todas as contas do mês
            });

            const contas = response.data?.data || response.data || [];
            
            // Filtrar contas que parecem ser mensalidades (por descrição ou categoria)
            const mensalidades = contas.filter(conta => {
                const descricaoLower = (conta.descricao || '').toLowerCase();
                const categoriaNome = (conta.categoria?.nome || '').toLowerCase();
                
                return descricaoLower.includes('mensalidade') || 
                       descricaoLower.includes('mensal') ||
                       categoriaNome.includes('mensalidade') ||
                       categoriaNome.includes('mensal');
            });

            if (mensalidades.length === 0) {
                // Se não há mensalidade no mês, verificar se há no próximo mês
                const nextMonthFirstDay = new Date(today.getFullYear(), today.getMonth() + 1, 1);
                const nextMonthLastDay = new Date(today.getFullYear(), today.getMonth() + 2, 0);
                
                const responseNextMonth = await financeiroApi.contaReceber.getAll({
                    paciente_id: pacienteId,
                    data_inicio: nextMonthFirstDay.toISOString().split('T')[0],
                    data_fim: nextMonthLastDay.toISOString().split('T')[0],
                    limit: 100
                });

                const contasNextMonth = responseNextMonth.data?.data || responseNextMonth.data || [];
                const mensalidadesNextMonth = contasNextMonth.filter(conta => {
                    const descricaoLower = (conta.descricao || '').toLowerCase();
                    const categoriaNome = (conta.categoria?.nome || '').toLowerCase();
                    
                    return descricaoLower.includes('mensalidade') || 
                           descricaoLower.includes('mensal') ||
                           categoriaNome.includes('mensalidade') ||
                           categoriaNome.includes('mensal');
                });

                if (mensalidadesNextMonth.length === 0) {
                    setStatus(null); // Sem mensalidade encontrada
                } else {
                    // Verificar status da mensalidade do próximo mês
                    const mensalidade = mensalidadesNextMonth[0];
                    const statusMensalidade = determinarStatus(mensalidade);
                    setStatus(statusMensalidade);
                }
            } else {
                // Verificar status da mensalidade do mês atual
                const mensalidade = mensalidades[0]; // Pegar a primeira mensalidade do mês
                const statusMensalidade = determinarStatus(mensalidade);
                setStatus(statusMensalidade);
            }
        } catch (err) {
            console.error('Erro ao buscar status de pagamento:', err);
            setError(err.message || 'Erro ao buscar status de pagamento');
            setStatus(null);
        } finally {
            setIsLoading(false);
        }
    }, [pacienteId]);

    useEffect(() => {
        fetchStatus();
    }, [fetchStatus]);

    return {
        status,
        isLoading,
        error,
        refetch: fetchStatus
    };
};

/**
 * Determina o status de pagamento baseado na conta
 * @param {Object} conta - Conta a receber
 * @returns {string|null} 'pago', 'atrasado', 'pendente' ou null
 */
const determinarStatus = (conta) => {
    if (!conta) return null;

    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    
    const dataVencimento = conta.data_vencimento ? new Date(conta.data_vencimento) : null;
    if (!dataVencimento) return null;

    dataVencimento.setHours(0, 0, 0, 0);

    // Se já foi recebido completamente
    if (conta.status === 'Recebido' || parseFloat(conta.valor_pendente || 0) <= 0) {
        return 'pago';
    }

    // Se está vencido e não foi pago
    if (dataVencimento < hoje && conta.status !== 'Recebido') {
        return 'atrasado';
    }

    // Se está dentro do mês e não foi pago
    const primeiroDiaMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    const ultimoDiaMes = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
    
    if (dataVencimento >= primeiroDiaMes && dataVencimento <= ultimoDiaMes) {
        return 'pendente';
    }

    // Se é do próximo mês
    const primeiroDiaProximoMes = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 1);
    const ultimoDiaProximoMes = new Date(hoje.getFullYear(), hoje.getMonth() + 2, 0);
    
    if (dataVencimento >= primeiroDiaProximoMes && dataVencimento <= ultimoDiaProximoMes) {
        return 'pendente';
    }

    return null;
};

/**
 * Hook para buscar status de pagamento de múltiplos pacientes (otimizado)
 * @param {Array<number>} pacienteIds - Array de IDs dos pacientes
 * @returns {Object} { statusMap, mensalidadeMap, isLoading, error, refetch }
 */
export const usePacientesPagamentoStatus = (pacienteIds = []) => {
    const [statusMap, setStatusMap] = useState({});
    const [mensalidadeMap, setMensalidadeMap] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchStatus = useCallback(async () => {
        if (!pacienteIds || pacienteIds.length === 0) {
            setStatusMap({});
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const today = new Date();
            const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
            const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
            
            // Buscar todas as contas a receber dos pacientes no mês atual
            const response = await financeiroApi.contaReceber.getAll({
                data_inicio: firstDayOfMonth.toISOString().split('T')[0],
                data_fim: lastDayOfMonth.toISOString().split('T')[0],
                limit: 1000 // Buscar muitas contas
            });

            const contas = response.data?.data || response.data || [];
            
            // Filtrar contas que são mensalidades
            const mensalidades = contas.filter(conta => {
                if (!pacienteIds.includes(conta.paciente_id)) return false;
                
                const descricaoLower = (conta.descricao || '').toLowerCase();
                const categoriaNome = (conta.categoria?.nome || '').toLowerCase();
                
                return descricaoLower.includes('mensalidade') || 
                       descricaoLower.includes('mensal') ||
                       categoriaNome.includes('mensalidade') ||
                       categoriaNome.includes('mensal');
            });

            // Criar mapa de status e valores de mensalidade por paciente
            const novoStatusMap = {};
            const novoMensalidadeMap = {};
            
            pacienteIds.forEach(pacienteId => {
                const mensalidadePaciente = mensalidades.find(m => m.paciente_id === pacienteId);
                
                if (mensalidadePaciente) {
                    novoStatusMap[pacienteId] = determinarStatus(mensalidadePaciente);
                    novoMensalidadeMap[pacienteId] = {
                        valor: parseFloat(mensalidadePaciente.valor_total || 0)
                    };
                } else {
                    // Verificar próximo mês se não encontrou no mês atual
                    novoStatusMap[pacienteId] = null; // Será atualizado se necessário
                    novoMensalidadeMap[pacienteId] = null;
                }
            });

            // Buscar também do próximo mês para pacientes sem mensalidade no mês atual
            const pacientesSemStatus = pacienteIds.filter(id => !novoStatusMap[id]);
            if (pacientesSemStatus.length > 0) {
                const nextMonthFirstDay = new Date(today.getFullYear(), today.getMonth() + 1, 1);
                const nextMonthLastDay = new Date(today.getFullYear(), today.getMonth() + 2, 0);
                
                const responseNextMonth = await financeiroApi.contaReceber.getAll({
                    data_inicio: nextMonthFirstDay.toISOString().split('T')[0],
                    data_fim: nextMonthLastDay.toISOString().split('T')[0],
                    limit: 1000
                });

                const contasNextMonth = responseNextMonth.data?.data || responseNextMonth.data || [];
                const mensalidadesNextMonth = contasNextMonth.filter(conta => {
                    if (!pacientesSemStatus.includes(conta.paciente_id)) return false;
                    
                    const descricaoLower = (conta.descricao || '').toLowerCase();
                    const categoriaNome = (conta.categoria?.nome || '').toLowerCase();
                    
                    return descricaoLower.includes('mensalidade') || 
                           descricaoLower.includes('mensal') ||
                           categoriaNome.includes('mensalidade') ||
                           categoriaNome.includes('mensal');
                });

                mensalidadesNextMonth.forEach(conta => {
                    novoStatusMap[conta.paciente_id] = determinarStatus(conta);
                    novoMensalidadeMap[conta.paciente_id] = {
                        valor: parseFloat(conta.valor_total || 0)
                    };
                });
            }

            setStatusMap(novoStatusMap);
            setMensalidadeMap(novoMensalidadeMap);
        } catch (err) {
            console.error('Erro ao buscar status de pagamento:', err);
            setError(err.message || 'Erro ao buscar status de pagamento');
            setStatusMap({});
        } finally {
            setIsLoading(false);
        }
    }, [pacienteIds]);

    useEffect(() => {
        fetchStatus();
    }, [fetchStatus]);

    return {
        statusMap,
        mensalidadeMap,
        isLoading,
        error,
        refetch: fetchStatus
    };
};

export default usePacientePagamentoStatus;

